import axios from 'axios';
import JSZip from 'jszip';
import md5 from 'md5';
import logger from '../logger';
import { FILE_RECOVERY_HTTPS_URL, useRecoveryServer } from '../recoveryServerUtils';

const GET_TIMEOUT = 2000;
const POST_TIMEOUT = 2000;

export enum DB_SAVE_REASONS {
  AUTO = 'auto_sync',
  BOX_CREATED = 'box_created',
  BOX_CLOSED = 'box_closed',
  BOX_DISPOSED = 'box_disposed',
  BOX_TRACKING_UPDATED = 'box_tracking_updated',
  BOUNDARY_UPDATED = 'boundary_updated',
  SAMPLE_BARCODE_SET = 'sample_barcode_set',
  SAMPLE_COMPLETED = 'sample_completed',
}

export interface FileEntryResponse {
  total: number;
  page: number;
  limit: number;
  mc_databases_list: string[];
}

class RecoveryServerDbManager {
  async listAvailableDbs(page: number, limit: number, search: string = ''): Promise<FileEntryResponse | undefined> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit), search });

    try {
      const response = await axios.get<FileEntryResponse>(
        `${FILE_RECOVERY_HTTPS_URL()}/mc-database/list?${params.toString()}`,
      );

      return response.data;
    } catch (error) {
      await logger.log('RecoveryServerDbManager:list error', error);

      return;
    }
  }

  // Sometimes, databases on the recovery server can be corrupted (e.g. incomplete upload).
  // This especially happens when an operator uses a "Soft Reset" (or "Hard Reset").
  // Although, the recovery server does its own validation since 2025-11-05, this
  // method has the retry mechanism to provide extra resilience.
  async fetchLatestDb(): Promise<{ filename: string; content: string } | undefined> {
    const maxTries = 5;

    for (let attempt = 0; attempt < maxTries; attempt++) {
      try {
        const result = await this.fetchDb(attempt);

        console.log(`RecoveryServerDbManager:fetchLatestDb - attempt ${attempt} succeeded`);
        await logger.log(`RecoveryServerDbManager:fetchLatestDb - attempt ${attempt} succeeded`);

        return result;
      } catch (error: any) {
        // For connection errors (server not available), don't retry
        if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNREFUSED') {
          console.warn(
            `RecoveryServerDbManager:fetchLatestDb - Recovery server not available (${error.code}). Skipping recovery server.`,
          );
          await logger.log(`RecoveryServerDbManager:fetchLatestDb - server not available: ${error.code}`);

          return undefined;
        }

        // Request timed out - this happens on mobile devices that are not connected to robot
        // (as opposed to ERR_NETWORK, which happens on desktops) - don't retry
        if (error?.code === 'ECONNABORTED' && error.message.includes('timeout')) {
          console.warn(`Request timed out after ${error.config?.timeout}ms`);
          await logger.log(`RecoveryServerDbManager:fetchLatestDb - timeout after ${error.config?.timeout}ms`);

          return undefined;
        }

        // Request was canceled by browser or AbortController - don't retry
        if (error?.code === 'ERR_CANCELED' || error?.name === 'AbortError') {
          console.warn(`RecoveryServerDbManager:fetchLatestDb - Request was canceled by the browser or aborted`);
          await logger.log(`RecoveryServerDbManager:fetchLatestDb - request canceled`);

          return undefined;
        }

        // If this was the last attempt, re-throw the error
        if (attempt === maxTries - 1) {
          return undefined;
        }

        // Otherwise, continue to the next attempt for other errors (like parsing errors)
        await logger.log(`RecoveryServerDbManager:fetchLatestDb - attempt ${attempt} failed, trying next`, error);
        console.warn(`Attempt ${attempt} failed, trying next`, error);
      }
    }

    return undefined;
  }

  async fetchDbWithFilename(filename: string): Promise<{ filename: string; content: string } | undefined> {
    try {
      return await this.fetchDb(0, filename);
    } catch (error) {
      return undefined;
    }
  }

  async saveDb(dbDataFile: Blob, saveReason: DB_SAVE_REASONS): Promise<boolean> {
    if (!useRecoveryServer()) {
      return false;
    }

    try {
      const dbData = await dbDataFile.text();

      const filename = new Date().getTime() + `-${saveReason || DB_SAVE_REASONS.AUTO}.json`;
      const saved = await this.uploadMcDatabase(filename, dbData);

      return saved;
    } catch (error) {
      await logger.log('RecoveryServerDbManager:save - error', { saveReason, error });

      return false;
    }
  }

  private async uploadMcDatabase(filename: string, content: string): Promise<boolean> {
    const zip = new JSZip();
    zip.file(filename, content);

    const zipFilename = `${filename}.zip`;
    const payload = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });

    const formData = new FormData();
    formData.append('file', payload, zipFilename);

    const response = await axios.post<string>(`${FILE_RECOVERY_HTTPS_URL()}/mc-database`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Cache-Control': 'no-cache',
      },
      timeout: POST_TIMEOUT,
    });

    const view = new Uint8Array(await payload.arrayBuffer());
    const hash = md5(view);

    if (response.data !== hash) {
      throw Error('Received hash did not match file!');
    }

    return true;
  }

  private async fetchDb(
    backIndex: number,
    search: string = '',
  ): Promise<{ filename: string; content: string } | undefined> {
    if (!useRecoveryServer()) {
      return undefined;
    }

    await logger.log('RecoveryServerDbManager:load - start', { backIndex });

    try {
      const result = await this.getMcDatabaseFile({ backIndex, search });

      if (!result) {
        console.warn('No file returned from the recovery server');
        await logger.log('RecoveryServerDbManager:load - no file returned from the server');

        return undefined; // Return undefined when no file is found (don't throw)
      }

      const { filename, content } = result;

      try {
        JSON.parse(content);
      } catch (err) {
        console.warn(`Could not parse recovery server DB file ${filename}:`, err);
        await logger.log(`RecoveryServerDbManager:load - could not parse the contents of file ${filename}`);

        // Throw error when JSON parsing fails (corrupted file) so we can retry
        throw new Error(`Could not parse recovery server DB file ${filename}: ${err}`);
      }

      await logger.log(`RecoveryServerDbManager:load - filename ${filename} has been loaded and parsed.`);

      return result;
    } catch (err) {
      console.warn('Error loading DB from recovery server:', err);
      await logger.log(`RecoveryServerDbManager:load - error`, err);

      // Re-throw the error so fetchLatestDb can handle retries
      throw err;
    }
  }

  private async getMcDatabaseFile({
    backIndex = 0,
    timeout = GET_TIMEOUT,
    search = '',
  }): Promise<{ filename: string; content: string } | undefined> {
    const response = await axios.get<ArrayBuffer>(`${FILE_RECOVERY_HTTPS_URL()}/mc-database`, {
      headers: {
        'Cache-Control': 'no-cache',
        Expires: '0',
      },
      params: {
        back_index: backIndex.toString(),
        search,
      },
      responseType: 'arraybuffer',
      timeout,
    });

    const zipFile = new File([response.data], 'temp.zip');

    const jsZip = new JSZip();
    const zip = await jsZip.loadAsync(zipFile);

    const jsonFile = Object.keys(zip.files).find((fileKey) => fileKey.endsWith('.json'));

    if (!jsonFile) {
      return undefined;
    }

    const content = await zip.files[jsonFile].async('text');

    return { filename: jsonFile, content };
  }
}

const recoveryServerDbManager = new RecoveryServerDbManager();
Object.freeze(recoveryServerDbManager);

export default recoveryServerDbManager;
